/**
 * Synapse API Client
 * Single source of truth for all Synapse backend communication
 */

const SYNAPSE_BASE_URL = import.meta.env.VITE_SYNAPSE_URL || 'http://localhost:8765';
const SYNAPSE_API_KEY = 'syn-4f8b2a9c1d6e3f7a';

class SynapseAPI {
  private baseURL: string;
  private apiKey: string;

  constructor(baseURL: string = SYNAPSE_BASE_URL, apiKey: string = SYNAPSE_API_KEY) {
    this.baseURL = baseURL;
    this.apiKey = apiKey;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      'X-API-Key': this.apiKey,
      ...options.headers,
    };

    const response = await fetch(url, { ...options, headers });

    if (!response.ok) {
      throw new Error(`Synapse API error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // Tasks
  async getTasks(limit?: number): Promise<any[]> {
    const query = limit ? `?limit=${limit}` : '';
    return this.request(`/tasks${query}`);
  }

  async getTask(taskId: string): Promise<any> {
    return this.request(`/tasks/${taskId}`);
  }

  async submitTask(description: string, priority: string = 'NORMAL'): Promise<any> {
    return this.request('/tasks', {
      method: 'POST',
      body: JSON.stringify({ description, priority }),
    });
  }

  async getTaskProgress(taskId: string): Promise<any> {
    return this.request(`/tasks/${taskId}/progress`);
  }

  async retryTask(taskId: string): Promise<any> {
    return this.request(`/tasks/${taskId}/retry`, { method: 'POST' });
  }

  // Config
  async getConfig(): Promise<any> {
    return this.request('/config');
  }

  async updateConfig(config: any): Promise<any> {
    return this.request('/config', {
      method: 'PATCH',
      body: JSON.stringify(config),
    });
  }

  async getFrontendConfig(): Promise<any> {
    return this.request('/config/frontend');
  }

  async getSynapseConfig(): Promise<any> {
    return this.request('/config/synapse');
  }

  // Metrics
  async getMetrics(): Promise<any> {
    return this.request('/metrics');
  }

  async getLLMMetrics(): Promise<any> {
    return this.request('/metrics/llm');
  }

  async getCostMetrics(): Promise<any> {
    return this.request('/metrics/cost');
  }

  // Status & Health
  async getStatus(): Promise<any> {
    return this.request('/status');
  }

  async getHealth(): Promise<any> {
    return this.request('/health');
  }

  // Control
  async pauseProcessing(): Promise<any> {
    return this.request('/control/pause', { method: 'POST' });
  }

  async resumeProcessing(): Promise<any> {
    return this.request('/control/resume', { method: 'POST' });
  }

  // WebSocket connections
  getTaskWebSocket(taskId: string): string {
    return `${this.baseURL.replace('http', 'ws')}/ws/tasks/${taskId}`;
  }

  getAllTasksWebSocket(): string {
    return `${this.baseURL.replace('http', 'ws')}/ws/tasks`;
  }

  // Feedback
  async getFeedback(): Promise<any> {
    return this.request('/api/feedback');
  }

  async submitFeedback(feedback: { project: string; message: string; email?: string }): Promise<any> {
    return this.request('/api/feedback', {
      method: 'POST',
      body: JSON.stringify(feedback),
    });
  }

  // Voice/Tasks
  async getVoiceTasks(): Promise<any[]> {
    return this.request('/voice/tasks');
  }

  async submitVoiceTask(query: string, priority: string = 'high'): Promise<any> {
    return this.request('/voice/queue', {
      method: 'POST',
      body: JSON.stringify({ query, priority, source: 'dashboard' }),
    });
  }

  // File Upload
  async uploadFile(filename: string, destination: string, content: string, encoding: string = 'base64'): Promise<any> {
    return this.request('/upload', {
      method: 'POST',
      body: JSON.stringify({ filename, destination, content, encoding }),
    });
  }

  // Claude Config Files
  async getClaudeConfig(file: 'personality' | 'snippets' | 'memory'): Promise<string> {
    const response = await fetch(`${this.baseURL}/claude/config?file=${file}`, {
      headers: { 'X-API-Key': this.apiKey },
    });
    return response.text();
  }

  async saveClaudeConfig(file: 'personality' | 'snippets' | 'memory', content: string): Promise<any> {
    return this.request('/claude/config', {
      method: 'POST',
      body: JSON.stringify({ file, content }),
    });
  }
}

// Export singleton instance
export const synapseAPI = new SynapseAPI();

// Export class for testing
export { SynapseAPI };
